import React, { useMemo } from "react";
import {
  Box,
  Paper,
  Typography,
  Avatar,
  Chip,
  Button,
  IconButton,
  Tooltip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { lightTheme } from "../../styles/theme";
import { getAvatarColor } from "../../utils/avatarUtils";
import {
  MANAGEMENT_AVATAR_FONT_SIZE,
  MANAGEMENT_AVATAR_SIZE,
  MANAGEMENT_TEXT_LINK_SX,
} from "../../styles/managementShared";
import {
  MANAGEMENT_ACTION_ROW_SX,
  MANAGEMENT_ACTION_ICON_BUTTON_SIZE,
  MANAGEMENT_ACTION_ICON_FONT_SIZE,
} from "../../styles/managementCardActions";

export interface JoinRequest {
  requestId: string;
  email: string;
  learnerName: string;
  learnerSurname: string;
  parentName: string;
  cellNumber: string;
  timestamp: string;
  status: string;
  gradeClass?: string;
  teacherName?: string;
  relationship?: string;
}

interface JoinRequestListProps {
  joinRequests: JoinRequest[];
  isMobile: boolean;
  isLoading: boolean;
  onAddUser?: (request: JoinRequest) => void;
  onDismiss?: (request: JoinRequest) => void;
  emptyTitle?: string;
  emptyDescription?: string;
  emptyActionLabel?: string;
  onEmptyAction?: () => void;
  emptyExtraContent?: React.ReactNode;
}

const createStyles = () => ({
  headerCell: {
    fontWeight: 600,
    color: "text.secondary",
    textTransform: "uppercase" as const,
    fontSize: "0.75rem",
    letterSpacing: "0.5px",
  },
  mobileCard: {
    p: 2,
    border: "1px solid",
    borderColor: "divider",
    borderRadius: 2,
    boxShadow: "none",
    display: "flex",
    alignItems: "center",
    flexDirection: "row" as const,
    gap: 2,
    minHeight: 56,
  },
  mobileCardContent: {
    flex: 1,
    minWidth: 0,
  },
  mobileCardName: {
    fontSize: "0.875rem",
  },
  mobileCardSubtext: {
    fontSize: "0.8125rem",
  },
  mobileCardDate: {
    fontSize: "0.625rem",
  },
  mobileDateContainer: {
    width: 80,
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    pr: 1,
  },
});

const formatDate = (dateString: string): string => {
  return new Date(dateString).toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

const getLearnerName = (request: JoinRequest): string =>
  `${request.learnerName} ${request.learnerSurname}`.trim();

const getInitials = (request: JoinRequest): string => {
  const first = request.learnerName?.[0] || "";
  const last = request.learnerSurname?.[0] || "";
  return (first + last).toUpperCase() || "?";
};

const DESKTOP_GRID_COLUMNS =
  "minmax(0, 1.5fr) minmax(0, 1.1fr) minmax(0, 2fr) minmax(0, 1.1fr) minmax(100px, 0.65fr) minmax(120px, 0.95fr) minmax(140px, auto)";

const desktopGridRowSx = {
  display: "grid",
  gridTemplateColumns: DESKTOP_GRID_COLUMNS,
  columnGap: 3,
  rowGap: 2,
  px: 3,
  alignItems: "center",
};

function RequestActions({
  request,
  onAddUser,
  onDismiss,
}: {
  request: JoinRequest;
  onAddUser?: (request: JoinRequest) => void;
  onDismiss?: (request: JoinRequest) => void;
}) {
  return (
    <Box sx={MANAGEMENT_ACTION_ROW_SX}>
      {onAddUser && (
        <Button sx={MANAGEMENT_TEXT_LINK_SX} onClick={() => onAddUser(request)}>
          Add user
        </Button>
      )}
      {onDismiss && (
        <Tooltip title="Dismiss request">
          <IconButton
            size={MANAGEMENT_ACTION_ICON_BUTTON_SIZE}
            onClick={() => onDismiss(request)}
            color="error"
          >
            <CloseIcon fontSize={MANAGEMENT_ACTION_ICON_FONT_SIZE} />
          </IconButton>
        </Tooltip>
      )}
    </Box>
  );
}

const JoinRequestList: React.FC<JoinRequestListProps> = React.memo(
  ({
    joinRequests,
    isMobile,
    isLoading,
    onAddUser,
    onDismiss,
    emptyTitle,
    emptyDescription,
    emptyActionLabel,
    onEmptyAction,
    emptyExtraContent,
  }) => {
    const styles = useMemo(() => createStyles(), []);

    if (isLoading) {
      return (
        <Typography variant="body2" sx={{ color: "text.secondary" }}>
          Loading requests...
        </Typography>
      );
    }

    if (joinRequests.length === 0) {
      return (
        <Box
          sx={{
            textAlign: "center",
            py: 4,
            color: "text.secondary",
          }}
        >
          <Typography variant="body1" sx={{ mb: 1 }}>
            {emptyTitle ?? "No join requests"}
          </Typography>
          <Typography variant="body2">
            {emptyDescription ??
              "When parents submit a join request from your public page, they will appear here."}
          </Typography>
          {emptyExtraContent}
          {emptyActionLabel && onEmptyAction ? (
            <Button
              variant="contained"
              onClick={onEmptyAction}
              sx={{ mt: 2.5 }}
            >
              {emptyActionLabel}
            </Button>
          ) : null}
        </Box>
      );
    }

    if (isMobile) {
      return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>
          {joinRequests.map((request) => {
            const learnerName = getLearnerName(request);

            return (
              <Paper
                key={request.requestId}
                elevation={0}
                sx={{
                  ...styles.mobileCard,
                  p: 1.5,
                  borderRadius: 1.5,
                  "&:hover": {
                    backgroundColor: "action.hover",
                  },
                }}
              >
                <Avatar
                  sx={{
                    width: MANAGEMENT_AVATAR_SIZE,
                    height: MANAGEMENT_AVATAR_SIZE,
                    backgroundColor: getAvatarColor(learnerName, lightTheme),
                    color: "common.white",
                    fontSize: MANAGEMENT_AVATAR_FONT_SIZE,
                    fontWeight: 600,
                    mr: 2,
                  }}
                >
                  {getInitials(request)}
                </Avatar>
                <Box sx={styles.mobileCardContent}>
                  <Typography
                    variant="subtitle2"
                    fontWeight={300}
                    noWrap
                    color="text.primary"
                    sx={styles.mobileCardName}
                  >
                    {learnerName}
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    noWrap
                    sx={styles.mobileCardSubtext}
                  >
                    {request.parentName} · {request.email}
                  </Typography>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      mt: 0.5,
                      flexWrap: "wrap",
                    }}
                  >
                    {request.gradeClass && (
                      <Chip
                        label={request.gradeClass}
                        size="small"
                        sx={{ fontSize: "0.7rem", height: 20 }}
                      />
                    )}
                    <Chip
                      label="Pending"
                      size="small"
                      color="warning"
                      sx={{ fontSize: "0.7rem", height: 20 }}
                    />
                  </Box>
                </Box>
                <Box sx={styles.mobileDateContainer}>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={styles.mobileCardDate}
                  >
                    {formatDate(request.timestamp)}
                  </Typography>
                </Box>
                {(onAddUser || onDismiss) && (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    {onAddUser && (
                      <Button
                        sx={MANAGEMENT_TEXT_LINK_SX}
                        onClick={() => onAddUser(request)}
                      >
                        Add user
                      </Button>
                    )}
                    {onDismiss && (
                      <Tooltip title="Dismiss request">
                        <IconButton
                          size={MANAGEMENT_ACTION_ICON_BUTTON_SIZE}
                          onClick={() => onDismiss(request)}
                          color="error"
                        >
                          <CloseIcon fontSize={MANAGEMENT_ACTION_ICON_FONT_SIZE} />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                )}
              </Paper>
            );
          })}
        </Box>
      );
    }

    return (
      <Box>
        <Box sx={{ ...desktopGridRowSx, py: 1, mb: 1 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2, minWidth: 0 }}>
            <Box
              sx={{
                width: MANAGEMENT_AVATAR_SIZE,
                height: MANAGEMENT_AVATAR_SIZE,
                flexShrink: 0,
              }}
              aria-hidden
            />
            <Typography sx={styles.headerCell}>Learner</Typography>
          </Box>
          <Typography sx={styles.headerCell}>Parent</Typography>
          <Typography sx={styles.headerCell}>Email</Typography>
          <Typography sx={styles.headerCell}>Phone</Typography>
          <Typography sx={{ ...styles.headerCell, whiteSpace: "nowrap" }}>
            Grade/Class
          </Typography>
          <Typography sx={{ ...styles.headerCell, whiteSpace: "nowrap" }}>
            Date
          </Typography>
          <Typography sx={{ ...styles.headerCell, textAlign: "right" }}>
            Actions
          </Typography>
        </Box>

        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {joinRequests.map((request) => {
            const learnerName = getLearnerName(request);

            return (
              <Paper
                key={request.requestId}
                elevation={0}
                sx={{
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 2,
                  "&:hover": {
                    backgroundColor: "action.hover",
                  },
                }}
              >
                <Box sx={{ ...desktopGridRowSx, py: 2 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 2, minWidth: 0 }}>
                    <Avatar
                      sx={{
                        width: MANAGEMENT_AVATAR_SIZE,
                        height: MANAGEMENT_AVATAR_SIZE,
                        backgroundColor: getAvatarColor(learnerName, lightTheme),
                        color: "common.white",
                        fontSize: MANAGEMENT_AVATAR_FONT_SIZE,
                        fontWeight: 600,
                        flexShrink: 0,
                      }}
                    >
                      {getInitials(request)}
                    </Avatar>
                    <Typography
                      variant="body2"
                      noWrap
                      sx={{ fontWeight: 500, minWidth: 0 }}
                    >
                      {learnerName}
                    </Typography>
                  </Box>

                  <Typography variant="body2" color="text.secondary" noWrap>
                    {request.parentName}
                  </Typography>

                  <Typography variant="body2" color="text.secondary" noWrap>
                    {request.email}
                  </Typography>

                  <Typography variant="body2" color="text.secondary" noWrap>
                    {request.cellNumber}
                  </Typography>

                  <Typography variant="body2" color="text.secondary" noWrap>
                    {request.gradeClass || "—"}
                  </Typography>

                  <Typography
                    variant="body2"
                    color="text.secondary"
                    noWrap
                    sx={{ fontSize: "0.8125rem" }}
                  >
                    {formatDate(request.timestamp)}
                  </Typography>

                  <RequestActions
                    request={request}
                    onAddUser={onAddUser}
                    onDismiss={onDismiss}
                  />
                </Box>
              </Paper>
            );
          })}
        </Box>
      </Box>
    );
  }
);

JoinRequestList.displayName = "JoinRequestList";

export default JoinRequestList;
